/**
 * TOP 7 - video slider player (sections with "Auto-advance when a video ends").
 *
 * - Every card that is on screen plays automatically (muted, looping): 4 on desktop,
 *   2 on phones. Click / tap a card to pause it, click again to continue.
 * - The card's cover image (.img-thumbnail) sits ON TOP of the <video>; the theme only
 *   hides it in its own play routine, which this section bypasses - so it is hidden
 *   here as soon as the clip really moves (otherwise the visitor only sees a still).
 * - The theme's desktop `.slide-overlay` layer covers each card and swallows clicks;
 *   clicks on a card are handled here first (capture phase) so pause/continue works.
 * - Phones: the row still glides on by one card when the left-most clip ends.
 *
 * Speed rules:
 * - Nothing loads before the page's own `load` event, so the section never competes
 *   with the product photos / LCP. When the slider is about one screen away the clips are fetched one by one
 *   (smallest HLS rendition, 480p - the cards are ~150px wide) so they are fully
 *   loaded by the time the visitor scrolls to them. Browsers without native HLS use
 *   the theme's MP4. Clips are HTTP-cached for a year, so loops cost nothing.
 * - If the device refuses autoplay (e.g. iPhone Low Power Mode) a play icon shows and
 *   a tap starts the clip.
 */
(() => {
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const HLS_TYPE = 'application/vnd.apple.mpegurl';
  const canHls = (() => {
    try { return !!document.createElement('video').canPlayType(HLS_TYPE); } catch (e) { return false; }
  })();

  // Page ready = `load` fired (+300ms), or 4s after this script ran, whichever is first.
  const pageReady = new Promise((resolve) => {
    const go = () => setTimeout(resolve, 300);
    if (document.readyState === 'complete') go();
    else window.addEventListener('load', go, { once: true });
    setTimeout(resolve, 4000);
  });

  // master .m3u8 -> absolute URL of its lowest-bandwidth rendition (cached per URL)
  const variantCache = new Map();
  const lowestRendition = (url) => {
    if (!variantCache.has(url)) {
      variantCache.set(url, fetch(url).then((r) => (r.ok ? r.text() : '')).then((txt) => {
        const lines = txt.split('\n');
        let best = null;
        let bw = Infinity;
        for (let i = 0; i < lines.length; i++) {
          const m = /^#EXT-X-STREAM-INF:.*?BANDWIDTH=(\d+)/.exec(lines[i]);
          const next = lines[i + 1] && lines[i + 1].trim();
          if (m && next && !next.startsWith('#') && +m[1] < bw) { bw = +m[1]; best = next; }
        }
        return best ? new URL(best, url).href : url;
      }).catch(() => url));
    }
    return variantCache.get(url);
  };

  const prepare = (v) => {
    if (v.__t7prep) return v.__t7prep;
    v.muted = true;
    v.playsInline = true;
    v.loop = false; // looping is done in JS so `ended` still fires for the phone auto-advance
    const hls = canHls && Array.from(v.querySelectorAll('source')).find((s) => s.type === HLS_TYPE && s.src);
    v.__t7prep = (hls ? lowestRendition(hls.src).then((u) => { if (v.getAttribute('src') !== u) v.src = u; }) : Promise.resolve())
      .then(() => {
        v.preload = 'auto';
        if (!hls && v.readyState === 0) { try { v.load(); } catch (e) { /* ignore */ } }
      });
    return v.__t7prep;
  };

  const ICON_PLAY = '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" style="fill:#173126 !important;margin-left:3px"><path d="M8 5v14l11-7z"/></svg>';
  const BADGE_CSS = 'position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);z-index:60;width:52px;height:52px;border-radius:50%;background:rgba(255,255,255,.92);box-shadow:0 6px 18px rgba(0,0,0,.25);display:flex;align-items:center;justify-content:center;pointer-events:none';

  const setup = (root) => {
    const id = root.getAttribute('data-t7vl-autoadvance');
    if (!id || root.dataset.t7vlAutoReady === '1') return;
    const row = root.querySelector('.slider-' + id);
    if (!row) return;

    const videoOf = (item) => Array.from(item.querySelectorAll('video')).find((v) => v.offsetParent !== null) || null;
    const items = Array.from(row.querySelectorAll('.slider-item-' + id))
      .filter((el) => !el.hasAttribute('is-clone') && el.querySelector('video'));
    if (!items.length) return;
    root.dataset.t7vlAutoReady = '1';

    const userPaused = new Set();
    const visible = new Set();
    let ready = false;
    let anim = 0;
    let programmatic = 0;
    let userHold = 0;
    let advanceLock = 0;
    let blocked = false;

    // ---- cover image + play badge ----
    const boxOf = (v) => v.closest('.external-video') || v.parentElement;
    const reveal = (v) => {
      const box = boxOf(v);
      if (box) box.querySelectorAll('.img-thumbnail').forEach((img) => img.classList.add('hidden'));
    };
    const badge = (item, show) => {
      const v = videoOf(item);
      const box = v && boxOf(v);
      if (!box) return;
      let b = box.querySelector(':scope > .t7vl-badge');
      if (show && !b) {
        if (getComputedStyle(box).position === 'static') box.style.position = 'relative';
        b = document.createElement('span');
        b.className = 't7vl-badge';
        b.style.cssText = BADGE_CSS;
        b.innerHTML = ICON_PLAY;
        box.appendChild(b);
      } else if (!show && b) b.remove();
    };

    // ---- smooth row glide (phones) ----
    const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
    const glideTo = (target) => {
      cancelAnimationFrame(anim);
      const start = row.scrollLeft;
      target = Math.max(0, Math.min(row.scrollWidth - row.clientWidth, target));
      const dist = target - start;
      if (REDUCED || Math.abs(dist) < 2) { row.scrollLeft = target; return; }
      const dur = Math.min(1100, 650 + Math.abs(dist) * 0.25);
      programmatic = Date.now() + dur + 300;
      row.style.scrollSnapType = 'none';
      const t0 = performance.now();
      const step = (now) => {
        const t = Math.min(1, (now - t0) / dur);
        row.scrollLeft = start + dist * ease(t);
        if (t < 1) anim = requestAnimationFrame(step);
        else row.style.scrollSnapType = '';
      };
      anim = requestAnimationFrame(step);
    };
    const hold = () => { userHold = Date.now() + 4000; cancelAnimationFrame(anim); row.style.scrollSnapType = ''; };
    row.addEventListener('touchstart', hold, { passive: true });
    row.addEventListener('wheel', () => { if (Date.now() < programmatic) hold(); }, { passive: true });

    // ---- play / pause ----
    const play = (item, fromUser) => {
      const v = videoOf(item);
      if (!v) return;
      item.querySelectorAll('video').forEach((o) => { if (o !== v && !o.paused) o.pause(); });
      prepare(v).then(() => {
        if (!fromUser && (!visible.has(item) || userPaused.has(item))) return;
        const p = v.play();
        if (p && p.catch) p.catch((err) => {
          if (err && err.name === 'NotAllowedError') { blocked = true; badge(item, true); }
        });
      });
    };
    const pause = (item) => item.querySelectorAll('video').forEach((v) => { if (!v.paused) v.pause(); });

    // Start the on-screen cards one after another (left to right); each waits until the
    // previous clip has really moved, so on mobile data the first card starts fastest.
    let chain = 0;
    const startVisible = () => {
      if (!ready || document.hidden) return;
      const run = ++chain;
      items.forEach((it) => { if (!visible.has(it)) pause(it); });
      const list = items.filter((it) => visible.has(it) && !userPaused.has(it));
      let i = 0;
      const nextOne = () => {
        if (run !== chain || i >= list.length) return;
        const it = list[i++];
        const v = videoOf(it);
        if (!v || (!v.paused && v.readyState > 2)) { nextOne(); return; }
        if (blocked) { badge(it, true); nextOne(); return; }
        let done = false;
        const tick = () => { if (v.currentTime > 0.25) go(); };
        const go = () => { if (done) return; done = true; v.removeEventListener('timeupdate', tick); nextOne(); };
        v.addEventListener('timeupdate', tick);
        setTimeout(go, 3000);
        play(it);
      };
      nextOne();
    };

    // ---- fetch every clip right after page load, one by one (on-screen cards first) ----
    const preloadAll = () => {
      // only the first cards a visitor sees (2 on phones, 4 on desktop) are buffered ahead; the rest load when they come on screen
      const order = items.slice().sort((a, b) => (visible.has(b) ? 1 : 0) - (visible.has(a) ? 1 : 0)).slice(0, window.innerWidth < 768 ? 3 : 5);
      let i = 0;
      const next = () => {
        if (i >= order.length) return;
        const v = videoOf(order[i++]);
        if (!v) { next(); return; }
        prepare(v).then(() => {
          if (v.readyState >= 4) { next(); return; }
          let done = false;
          const go = () => { if (done) return; done = true; next(); };
          v.addEventListener('canplaythrough', go, { once: true });
          v.addEventListener('error', go, { once: true });
          setTimeout(go, 4000);
        });
      };
      next();
    };

    // ---- per-video events ----
    items.forEach((item) => {
      item.querySelectorAll('video').forEach((v) => {
        v.addEventListener('timeupdate', () => { if (v.currentTime > 0.05) reveal(v); });
        v.addEventListener('playing', () => {
          if (blocked) { blocked = false; items.forEach((it) => { if (!userPaused.has(it)) badge(it, false); }); startVisible(); }
        });
        v.addEventListener('ended', () => {
          if (!visible.has(item) || userPaused.has(item)) return;
          const leftmost = items.find((it) => visible.has(it));
          if (item === leftmost && row.scrollWidth > row.clientWidth + 4 && Date.now() > userHold && Date.now() > advanceLock) {
            advanceLock = Date.now() + 3500; // two clips ending close together must not slide twice
            const max = row.scrollWidth - row.clientWidth;
            const nextItem = items[items.indexOf(item) + 1];
            glideTo(row.scrollLeft >= max - 4 || !nextItem
              ? 0
              : row.scrollLeft + (nextItem.getBoundingClientRect().left - item.getBoundingClientRect().left));
          }
          try { v.currentTime = 0; } catch (e) { /* ignore */ }
          const p = v.play();
          if (p && p.catch) p.catch(() => {});
        });
      });

      // Click / tap = pause, again = continue. Capture phase, so the theme's desktop
      // slide overlay (goToSlide) and its own togglePlay never see this click.
      item.addEventListener('click', (e) => {
        if (e.target.closest('a, button, form, input, select, textarea, .button-sound-control')) return;
        const v = videoOf(item);
        if (!v) return;
        e.preventDefault();
        e.stopPropagation();
        if (v.paused) {
          userPaused.delete(item);
          blocked = false;
          badge(item, false);
          play(item, true);
        } else {
          userPaused.add(item);
          v.pause();
          reveal(v);
          badge(item, true);
        }
      }, true);
    });

    // ---- which cards are on screen (IntersectionObserver + rect check as backup) ----
    const measure = () => {
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const vw = window.innerWidth || document.documentElement.clientWidth;
      const rr = row.getBoundingClientRect();
      let changed = false;
      items.forEach((it) => {
        const b = it.getBoundingClientRect();
        const w = Math.max(0, Math.min(b.right, rr.right, vw) - Math.max(b.left, rr.left, 0));
        const h = Math.max(0, Math.min(b.bottom, vh) - Math.max(b.top, 0));
        const on = b.width > 0 && b.height > 0 && w * h >= 0.5 * b.width * b.height;
        if (on !== visible.has(it)) { changed = true; if (on) visible.add(it); else visible.delete(it); }
      });
      if (changed) startVisible();
    };
    let raf = 0;
    const schedule = () => { if (!raf) raf = requestAnimationFrame(() => { raf = 0; measure(); }); };
    const io = new IntersectionObserver(schedule, { threshold: [0, 0.5, 1] });
    items.forEach((it) => io.observe(it));
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    row.addEventListener('scroll', schedule, { passive: true });

    // fetch the clips only when the slider is about to come on screen (not right after page load), so a visitor
    // who never scrolls this far downloads nothing (2026-10-09)
    let nearDone = false;
    const nearIo = new IntersectionObserver((es) => {
      if (nearDone || !ready || !es.some((e) => e.isIntersecting)) return;
      nearDone = true; nearIo.disconnect(); preloadAll();
    }, { rootMargin: '0px 0px 35% 0px' });   // the slider is less than ~1/3 screen away
    pageReady.then(() => {
      ready = true;
      measure();
      startVisible();
      nearIo.observe(root);
      // Safety net: every 1.5s make sure the visible, not user-paused cards are running.
      setInterval(() => {
        if (document.hidden) return;
        measure();
        items.forEach((it) => {
          const v = videoOf(it);
          if (v && visible.has(it) && v.paused && !userPaused.has(it) && !blocked && v.readyState >= 2) play(it);
        });
      }, 1500);
    });

    // Pause everything while the tab is hidden; resume the visible cards on return.
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) items.forEach(pause);
      else { measure(); startVisible(); }
    });
  };

  const boot = () => document.querySelectorAll('[data-t7vl-autoadvance]').forEach(setup);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  // Theme editor re-renders and the buy box's section relocation both re-add nodes.
  document.addEventListener('shopify:section:load', boot);
  setTimeout(boot, 1200);
})();
